/**
 * SolutionsFirstSteps — "Mitä tehdään ensiksi?" universal action view.
 *
 * Aggregates the first-phase (0–3 kk) actions across ALL solutions' implementation plans,
 * grouped by responsible organisation. Gives the reader a single place to answer:
 * "If we started this week — who does what?"
 *
 * Data source: workshop_solutions.ai_analysis.implementationPlan.timeline[0] OR phases[0].
 * - Structured shape (timeline): action.who / action.what / action.deliverable
 * - Legacy shape (phases):       action is a string; we text-match against the plan's
 *                                keyOrgs / leadOrganisations list to attribute.
 */
import { useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { Building2, Zap, ChevronDown, ChevronUp, ChevronRight, Brain } from 'lucide-react';
import { Section, SectionTitle, Paragraph } from './reportStyles';
import { sanitizeAiText } from '../../utils/sanitizeAiText';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import EditableText from './EditableText';

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const AiPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 2px;
  color: #6d28d9;
  background: #f3e8ff;
  border: 1px solid #e9d5ff;
  flex-shrink: 0;
`;

const Disclaimer = styled.div`
  font-size: 12px;
  color: #9ca3af;
  font-style: italic;
  margin-bottom: 16px;
`;

const Intro = styled.div`
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 16px;
  line-height: 1.6;
`;

const OrgList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const OrgBlock = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-left: 4px solid #367ABA;
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  @media print { page-break-inside: avoid; box-shadow: none; }
`;

const OrgHeader = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 14px 20px;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
  font-family: inherit;

  &:hover { background: #f9fafb; }
  @media print { cursor: default; &:hover { background: transparent; } }
`;

const OrgBody = styled.div`
  padding: 0 20px 16px;
  border-top: 1px solid #f3f4f6;
  @media print { display: block !important; }
`;

const Chevron = styled.span`
  display: inline-flex;
  color: #9ca3af;
  transition: transform 0.15s ease;
  transform: rotate(${p => (p.$open ? 90 : 0)}deg);
`;

const OrgName = styled.div`
  font-weight: 700;
  color: #1f2937;
  font-size: 15px;
  flex: 1;
`;

const ActionCount = styled.div`
  background: #f5f5f5;
  color: #4338ca;
  font-size: 12px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 2px;
`;

const ActionList = styled.ul`
  margin: 0;
  padding: 0 0 0 4px;
  list-style: none;
`;

const ActionItem = styled.li`
  display: flex;
  gap: 8px;
  padding: 8px 0;
  border-top: 1px dashed #f3f4f6;
  font-size: 14px;
  line-height: 1.55;
  color: #374151;

  &:first-of-type { border-top: 0; padding-top: 4px; }
`;

const SolutionTag = styled.span`
  display: inline-block;
  background: #f3f4f6;
  color: #6b7280;
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 2px;
  margin-left: 6px;
  white-space: nowrap;
`;

const EmptyNote = styled.div`
  padding: 16px;
  background: #fafafa;
  border: 1px dashed #e5e7eb;
  border-radius: 2px;
  color: #9ca3af;
  font-size: 13px;
`;

const Expand = styled.button`
  margin-top: 16px;
  background: transparent;
  border: 1px solid #d1d5db;
  color: #4b5563;
  font-size: 13px;
  padding: 6px 12px;
  border-radius: 2px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &:hover { background: #f9fafb; }
`;

const MAX_ORGS_COLLAPSED = 8;

// Extract all actions from a plan across every phase. Each action keeps
// its phase label so the UI can mark when a task belongs to 0–3 kk vs
// 3–6 kk vs 7–12 kk — broadening beyond the first-phase-only scope means
// every linked organisation gets a chance to appear.
function allPhaseActions(plan) {
  if (!plan) return [];
  const phases = plan.timeline?.length ? plan.timeline : plan.phases || [];
  const out = [];
  for (const phase of phases) {
    if (!phase?.actions?.length) continue;
    const phaseLabel = phase.phase || phase.label || '';
    for (const a of phase.actions) {
      const obj = typeof a === 'string' ? { what: a } : a;
      out.push({ ...obj, phase: phaseLabel });
    }
  }
  return out;
}

function planOrgs(plan) {
  const lead = plan?.leadOrganisations || [];
  const keyOrgs = plan?.keyOrgs || [];
  return [
    ...lead.map(o => (typeof o === 'string' ? o : o.orgName)),
    ...keyOrgs.map(o => (typeof o === 'string' ? o : o.orgName)),
  ].filter(Boolean);
}

// Strip trailing descriptors like " — koordinaatiovastuu" from org badges
function normalizeOrgName(name) {
  if (!name) return '';
  return name.split(/[—–-]/)[0].trim();
}

// For string-shape actions: find which orgs are mentioned in the text.
function orgsMentionedIn(text, candidateOrgs) {
  const found = [];
  for (const org of candidateOrgs) {
    const norm = normalizeOrgName(org);
    if (!norm) continue;
    // Match on normalized name, case-insensitive, word-boundary-ish
    const re = new RegExp(
      norm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
      'i'
    );
    if (re.test(text)) found.push(norm);
  }
  return found;
}

function buildIndex(solutions, organisations, orgActions) {
  // Map<orgName, Array<{ solutionTitle, text, phase, source }>>
  const byOrg = new Map();
  const unattributed = [];

  // 1) Walk implementation-plan timeline actions across all solutions.
  for (const sol of solutions || []) {
    const plan = sol?.ai_analysis?.implementationPlan;
    const actions = allPhaseActions(plan);
    if (!actions.length) continue;

    const solTitle =
      sol.metadata?.ai_title ||
      (sol.title || '').split(/[.!?\n]/)[0].slice(0, 80) ||
      'Ratkaisu';
    const candidateOrgs = planOrgs(plan).map(normalizeOrgName);

    for (const a of actions) {
      const text = sanitizeAiText(a.what || a.action || '');
      if (!text) continue;

      let attributedOrgs = [];
      if (a.who) attributedOrgs = [normalizeOrgName(a.who)];
      else attributedOrgs = orgsMentionedIn(text, candidateOrgs);

      if (attributedOrgs.length === 0) {
        unattributed.push({ solTitle, text, phase: a.phase, source: 'plan' });
        continue;
      }
      for (const org of attributedOrgs) {
        if (!byOrg.has(org)) byOrg.set(org, []);
        byOrg.get(org).push({ solTitle, text, phase: a.phase, source: 'plan' });
      }
    }
  }

  // 2) Merge in organisation_action_items. These are the AI-generated
  //    "what could THIS org do across the workshop's solutions" items
  //    generated per-org from their profile + the workshop context.
  //    Primary use: orgs without plan-timeline attribution still get tasks.
  const solutionTitleById = new Map();
  for (const sol of solutions || []) {
    solutionTitleById.set(
      sol.id,
      sol.metadata?.ai_title ||
        (sol.title || '').split(/[.!?\n]/)[0].slice(0, 80) ||
        'Ratkaisu'
    );
  }
  const orgNameById = new Map();
  for (const o of organisations || []) {
    if (o?.id && o?.name) orgNameById.set(o.id, normalizeOrgName(o.name));
  }
  for (const act of orgActions || []) {
    const orgName = orgNameById.get(act.organisation_id);
    if (!orgName) continue;
    const text = sanitizeAiText(act.action_text || '');
    if (!text) continue;
    if (!byOrg.has(orgName)) byOrg.set(orgName, []);
    byOrg.get(orgName).push({
      solTitle: solutionTitleById.get(act.solution_id) || 'Yleinen',
      text,
      phase: act.urgency === 'now' ? 'Heti' : null,
      source: 'org-action',
      actionId: act.id,
    });
  }

  // 3) Seed every participating organisation so none are invisible.
  const linkedNames = (organisations || [])
    .map(o => normalizeOrgName(o?.name))
    .filter(Boolean);
  for (const name of linkedNames) {
    if (!byOrg.has(name)) byOrg.set(name, []);
  }

  // Sort: orgs with tasks first (by task count desc), then no-task orgs
  // alphabetically.
  const sorted = [...byOrg.entries()]
    .map(([org, items]) => ({ org, items }))
    .sort((a, b) => {
      if (a.items.length !== b.items.length) return b.items.length - a.items.length;
      return a.org.localeCompare(b.org, 'fi');
    });

  return { byOrg: sorted, unattributed };
}

function OrgCard({ org, items }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const hasItems = items.length > 0;
  return (
    <OrgBlock>
      <OrgHeader
        type="button"
        onClick={() => setOpen(v => !v)}
        aria-expanded={open}
      >
        <Building2 size={18} style={{ color: hasItems ? '#367ABA' : '#9ca3af' }} />
        <OrgName>{org}</OrgName>
        <ActionCount style={hasItems ? undefined : { background: '#f3f4f6', color: '#9ca3af' }}>
          {hasItems ? t('report.firstSteps.actionCount', { count: items.length }) : t('report.firstSteps.collaborationCta')}
        </ActionCount>
        <Chevron $open={open}>
          <ChevronRight size={16} />
        </Chevron>
      </OrgHeader>
      {open && (
        <OrgBody>
          <ActionList>
            {items.map((item, i) => (
              <ActionItem key={i}>
                <Zap
                  size={14}
                  style={{
                    color: '#fbbf24',
                    flexShrink: 0,
                    marginTop: 3,
                  }}
                />
                <div>
                  {item.actionId
                    ? <EditableText fieldKey={`orgAction.${item.actionId}.action_text`} fallback={item.text} />
                    : item.text}
                  {item.phase && (
                    <SolutionTag style={{ background: '#f5f5f5', color: '#4338ca' }}>
                      {item.phase}
                    </SolutionTag>
                  )}
                  <SolutionTag>{item.solTitle}</SolutionTag>
                </div>
              </ActionItem>
            ))}
            {/* Universal collaboration CTA — always last item on every org */}
            <ActionItem style={{ borderTop: '1px dashed #f5f5f5', paddingTop: 10, marginTop: 4 }}>
              <Zap
                size={14}
                style={{ color: '#367ABA', flexShrink: 0, marginTop: 3 }}
              />
              <div style={{ color: '#4338ca', fontWeight: 500 }}>
                {t('report.firstSteps.findSolution')}{' '}
                <a
                  href="#solutions"
                  style={{ color: '#4338ca', textDecoration: 'underline' }}
                >
                  {t('report.firstSteps.browseSolutions')}
                </a>
                .
              </div>
            </ActionItem>
          </ActionList>
        </OrgBody>
      )}
    </OrgBlock>
  );
}

export default function SolutionsFirstSteps({ solutions, organisations }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const { byOrg, unattributed } = useMemo(
    () => buildIndex(solutions, organisations),
    [solutions, organisations]
  );

  if (byOrg.length === 0 && unattributed.length === 0) return null;

  const orgsWithTasks = byOrg.filter(o => o.items.length > 0);
  const orgsWithoutTasks = byOrg.filter(o => o.items.length === 0);
  const visibleOrgs = expanded ? byOrg : byOrg.slice(0, MAX_ORGS_COLLAPSED);
  const hiddenCount = byOrg.length - visibleOrgs.length;
  const totalActions =
    byOrg.reduce((n, o) => n + o.items.length, 0) + unattributed.length;

  return (
    <Section id="first-steps">
      <TitleRow>
        <SectionTitle style={{ margin: 0 }}>{t('report.firstSteps.title')}</SectionTitle>
        <AiPill title={t('report.firstSteps.aiDisclaimer')}>
          <Brain size={12} />
          {t('report.firstSteps.aiDisclaimer')}
        </AiPill>
      </TitleRow>
      <Disclaimer>
        {t('report.firstSteps.disclaimer')}
      </Disclaimer>
      <Intro>{t('report.firstSteps.intro')}</Intro>

      {byOrg.length === 0 ? (
        <EmptyNote>
          {t('report.firstSteps.noOrgAttribution')}
        </EmptyNote>
      ) : (
        <>
          <OrgList>
            {byOrg.map(({ org, items }) => (
              <OrgCard key={org} org={org} items={items} />
            ))}
          </OrgList>

          {unattributed.length > 0 && (
            <Paragraph style={{ marginTop: 20, fontSize: 13, color: '#9ca3af' }}>
              {t('report.firstSteps.unattributedNote', { count: unattributed.length })}
            </Paragraph>
          )}
        </>
      )}
    </Section>
  );
}
